/**
 * useMembership — React hook exposing the user's effective NeuroHeal tier.
 *
 * Reads from `/api/membership/me` and returns a stable object with flags
 * components can use to gate features without each one reimplementing the
 * tier logic. Returns `{ loading, tier, label, isPremium, isCoreOrAbove,
 * isTrial, expiresAt, hasFeature(name), refresh() }`.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';

const DEFAULT = {
  loading: true,
  tier: 'expired',
  label: '',
  isPremium: false,
  isCoreOrAbove: false,
  isTrial: false,
  expiresAt: null,
  features: [],
  source: 'none',
};

export const useMembership = () => {
  const [state, setState] = useState(DEFAULT);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/membership/me');
      const d = res.data || {};
      setState({
        loading: false,
        tier: d.tier || 'expired',
        label: d.label || '',
        isPremium: !!d.is_premium,
        isCoreOrAbove: !!d.is_core_or_above,
        isTrial: !!d.is_trial,
        expiresAt: d.expires_at || null,
        features: d.features || [],
        source: d.source || 'none',
      });
    } catch (_e) {
      setState({ ...DEFAULT, loading: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hasFeature = useCallback(
    (name) => state.features.includes(name),
    [state.features],
  );

  return { ...state, hasFeature, refresh: load };
};
